import { useCallback, useState } from "react";
import { DefaultButton, IconButton, PrimaryButton } from "@fluentui/react";
import { t } from "i18next";

import { DEVICE_OPTIONS } from "../../helpers/hangingProtocol/protocolEnums";
import { BoardSortable, SortableRow, openHandlers } from "./boardRow";
import ProtocolTable from "./ProtocolTable";
import "./groupBoard.css";

/**
 * Hanging protocol groups as a single-line list, each expanding to the protocols
 * it owns.
 *
 * A read-only board has no Action column at all, header included: every act in that
 * column belongs to whoever may write the list, and a column of controls nobody here
 * may press is dead furniture holding a third of the row open. What those buttons also
 * said — whether a group is live — is said in the row instead, beside the name.
 *
 * Written rather than assembled out of the shared list view for two reasons. The
 * shared table stacks every field of a row on two lines, so a group took as much
 * height as it had fields to show; here a group is one line and its protocols
 * appear beneath it only when asked for. And reordering by drag needs one drag
 * context around all the rows at a level, which a table built out of per-cell
 * renderers cannot provide.
 *
 * Order is data, not presentation: matching is first-applicable-wins at both
 * levels, so dragging a group or a protocol changes what hangs. That is why the
 * positions are numbered.
 *
 * The protocols themselves are a table of their own, in ProtocolTable — the same
 * app table one rank down, in the tones the app keeps for nested rows. It is a
 * component rather than a block here because it has its own columns, its own
 * header and its own drag context, and because a group is easier to read next to
 * the protocols it owns than inside them.
 *
 * Every action on a protocol that is not editing its body is still owned here —
 * adding one to a group, removing one, reordering — because this is the one screen
 * that shows a group next to those protocols. Adding sits on the group line rather
 * than in the table below: a group with no protocols yet has no drawer to open,
 * and that is exactly when one is added.
 *
 * None of them writes. Reordering, activating and deleting all report what was
 * done and are shown back whatever the caller makes of it — the group list
 * holds them as a draft until Save. A row the caller has marked for deletion says
 * so by `pendingDelete`: it keeps its place, struck through and out of the
 * numbering, and its delete button becomes the undo. That is the whole of what
 * makes a deletion answerable here — the row is still on screen, next to the ones
 * it was competing with, for as long as it takes to change your mind.
 *
 * The line opens what it names, to read: a group line opens the group, a protocol
 * row opens the protocol, and both land on the wizard in view mode, which is where
 * its Edit button is. That is the order every other list in the app puts the two
 * acts in, and it is why there is no pencil in the action column any more — the
 * column is left to the acts the wizard cannot do: add a protocol, delete a row.
 *
 * A press that lands on one of those controls is that control's and never the
 * row's, which is what a line carrying real buttons has to settle. The row is
 * reachable from the keyboard in its own right, so removing the pencil took
 * nothing away from anyone who does not use a mouse.
 *
 * Drag starts at the grip and nowhere else: the line carries real controls, so
 * making the whole line the drag source would swallow them. The grip is a button,
 * which is also what makes reordering reachable from the keyboard through the
 * dnd-kit KeyboardSensor.
 *
 * A reorder is reported as a pair of ids rather than a new order: the caller
 * owns the full list, of which this is one page, and is the only place that can
 * turn a move into the complete permutation the server requires.
 */

const DEVICE_LABEL = DEVICE_OPTIONS.reduce((all, option) => ({ ...all, [option.value]: option.label }), {});

const deviceNames = (group) => (group.devices ?? []).map((device) => DEVICE_LABEL[device] ?? device).join(", ");

export default function GroupBoard({
  groups = [],
  readOnly = false,
  onOpenGroup,
  onOpenProtocol,
  onAddProtocol,
  onDeleteProtocol,
  onRestoreProtocol,
  onReorderGroups,
  onReorderProtocols,
  onToggleActive,
  onDelete,
  onRestore,
  emptyMessage,
}) {
  // View state, so it survives the list refresh that a save or an activation runs.
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  const toggle = useCallback((groupId) => {
    setExpandedIds((current) => {
      const next = new Set(current);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  }, []);

  const onGroupDragEnd = ({ active, over }) => {
    if (over && active.id !== over.id) onReorderGroups?.(active.id, over.id);
  };

  return (
    <div className={`hpv2-board ${readOnly ? "is-readonly" : ""}`}>
      <div className="hpv2-board-scroll">
        <div className="hpv2-board-row hpv2-board-head">
          <span />
          {/* Only the word: the rule it follows from is the footer hint, which has the
              room for a sentence. Spelled out here it wrapped to four lines. */}
          <span title={t("hanging-protocol.group-order-hint")}>{t("hanging-protocol.order")}</span>
          <span />
          <span>{t("hanging-protocol.group")}</span>
          <span>{t("hanging-protocol.devices")}</span>
          {/* Status is not a column of its own any more: the button that changes the
              state is also the one place it is said, so it stands under Action with
              the rest of what can be done to the row.

              And no Action at all where nothing can be done: on a read-only board this
              header goes with the buttons that were under it. */}
          {!readOnly && <span>{t("supervisor.action")}</span>}
        </div>

        {groups.length === 0 && (
          <p className="hpv2-board-empty">{emptyMessage ?? t("hanging-protocol.no-groups")}</p>
        )}

        <BoardSortable ids={groups.map((group) => group.id)} onDragEnd={onGroupDragEnd}>
          <ul className="hpv2-board-list">
            {groups.map((group) => {
              const protocols = group.protocols ?? [];
              const expanded = expandedIds.has(group.id);
              const empty = protocols.length === 0;
              const openGroup = () => onOpenGroup?.(group);
              const going = Boolean(group.pendingDelete);
              const toggleLabel = t(
                expanded ? "hanging-protocol.hide-protocols" : "hanging-protocol.show-protocols"
              );

              return (
                <SortableRow
                  key={group.id}
                  id={group.id}
                  // A row on its way out is not dragged: it is in no order that Save will
                  // send, so the only thing to do with it is keep it or undo it.
                  disabled={readOnly || going}
                  className={`hpv2-board-group ${group.active === false ? "is-inactive" : ""} ${
                    going ? "is-pending-delete" : ""
                  }`}
                  gripLabel={`${t("hanging-protocol.drag-to-reorder")} ${group.name ?? ""}`}
                >
                  {(grip) => (
                    <>
                      <div
                        className="hpv2-board-row hpv2-board-group-row"
                        data-testid={`hpv2-group-${group.id}`}
                        role="button"
                        tabIndex={0}
                        title={t("hanging-protocol.view-group")}
                        aria-label={`${t("hanging-protocol.view-group")} ${group.name ?? ""}`}
                        {...openHandlers(openGroup)}
                      >
                        {grip}

                        {/* No number while it is leaving: it is out of the matching order
                            already, and renumbering the rows under it twice — once now,
                            once on Save — would read as the drag nobody made. */}
                        <span className="hpv2-board-cell hpv2-board-order">
                          {going ? "—" : group.position}
                        </span>

                        <IconButton
                          className={`hpv2-board-toggle ${empty ? "is-empty" : ""}`}
                          iconProps={{ iconName: expanded ? "ChevronDown" : "ChevronRight" }}
                          title={toggleLabel}
                          ariaLabel={`${toggleLabel} ${group.name ?? ""}`}
                          aria-expanded={expanded}
                          disabled={empty}
                          onClick={() => toggle(group.id)}
                        />

                        <span className="hpv2-board-cell" title={group.description || group.name || ""}>
                          <span className="hpv2-board-name">{group.name || "-"}</span>
                          {/* On a read-only board this is the only place the state is said:
                              the button that said it went with the rest of the column, and
                              an inactive group hangs for nobody — which a reader who cannot
                              change it still has to be able to see. Only when it is off;
                              "Active" on every other row would be noise. */}
                          {readOnly && group.active === false && (
                            <span className="hpv2-board-inactive">{t("hospital.inactive")}</span>
                          )}
                          {group.description && <span className="hpv2-board-sub">{group.description}</span>}
                        </span>

                        <span className="hpv2-board-cell" title={deviceNames(group)}>
                          {(group.devices ?? []).length === 0
                            ? t("hanging-protocol.devices-all")
                            : deviceNames(group)}
                        </span>

                        {/* One column, and every act on the group in it as a button that
                            says what it does.

                            The state was a column of its own next to this one, which said
                            the same thing twice: a row reading Active beside a button that
                            would deactivate it. The button is the state now — it names the
                            one move the row has left, which is how the rest of the platform
                            puts it, and its own word says which way the row currently is.

                            Opening the group is not here: the line does that. What is left
                            is what the wizard cannot do — turn the group on or off, give it
                            a protocol, take it away — and none of it is a reader's to do,
                            so a read-only board has no such column and its rows are that
                            much shorter. The line still opens every group. */}
                        {!readOnly && (
                          <span className="hpv2-board-actions">
                            {/* Activate is the primary of the two, as it is on every other
                                list here: an inactive group hangs for nobody, so turning it
                                back on is the move that row is waiting for. */}
                            {group.active ? (
                              <DefaultButton
                                className="hpv2-board-status"
                                text={t("hospital.deactivate")}
                                ariaLabel={`${t("hanging-protocol.toggle-active")} ${group.name ?? ""}`}
                                disabled={going}
                                onClick={() => onToggleActive?.(group, false)}
                              />
                            ) : (
                              <PrimaryButton
                                className="hpv2-board-status"
                                text={t("hospital.activate")}
                                ariaLabel={`${t("hanging-protocol.toggle-active")} ${group.name ?? ""}`}
                                disabled={going}
                                onClick={() => onToggleActive?.(group, true)}
                              />
                            )}

                            {/* Protocols are owned here, so they are also created
                                here — and from the group line rather than the
                                table below, which a group with none of them yet
                                cannot open. Not into a group that is leaving. */}
                            <DefaultButton
                              className="hpv2-board-add"
                              text={t("hanging-protocol.add-protocol")}
                              ariaLabel={`${t("hanging-protocol.add-protocol")} ${group.name ?? ""}`}
                              disabled={going}
                              onClick={() => onAddProtocol?.(group)}
                            />
                            {/* The same button either way, because it is the same
                                decision: nothing has happened to the row yet, so the
                                way back is where the way out was. Both wear the same
                                outline as the two beside them — a destructive act must
                                not carry more weight in the row than the ones people
                                came for, so the red is on approach only. */}
                            {going ? (
                              <DefaultButton
                                className="hpv2-board-restore"
                                text={t("hanging-protocol.undo-delete")}
                                ariaLabel={`${t("hanging-protocol.undo-delete")} ${group.name ?? ""}`}
                                data-testid={`hpv2-group-restore-${group.id}`}
                                onClick={() => onRestore?.(group)}
                              />
                            ) : (
                              <DefaultButton
                                className="hpv2-board-delete"
                                text={t("hanging-protocol.delete-action")}
                                ariaLabel={`${t("hanging-protocol.delete")} ${group.name ?? ""}`}
                                onClick={() => onDelete?.(group)}
                              />
                            )}
                          </span>
                        )}
                      </div>

                      {expanded && (
                        <ProtocolTable
                          group={group}
                          protocols={protocols}
                          readOnly={readOnly}
                          groupLeaving={going}
                          onOpenProtocol={onOpenProtocol}
                          onDeleteProtocol={onDeleteProtocol}
                          onRestoreProtocol={onRestoreProtocol}
                          onReorderProtocols={onReorderProtocols}
                        />
                      )}
                    </>
                  )}
                </SortableRow>
              );
            })}
          </ul>
        </BoardSortable>
      </div>
    </div>
  );
}
